import { DoNotDisturbAltOutlined } from '@mui/icons-material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card, Typography } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { handleError } from '../../../shared/functions/handleError';
import { useAttendanceColumns } from '../../../shared/hooks/useAttendanceColumns';
import { usePagination } from '../../../shared/hooks/usePagination';
import { IAttendance } from '../../../shared/interfaces/attendance/IAttendance';
import i18n from '../../../utils/localization/i18n';
import AttendanceManualFormModal from './components/AttendanceManualFormModal';
import { AdminAttendanceService } from './service';

const AdminAttendance = () => {
  const { t } = useTranslation();
  const {
    pagination,
    setPagination,
    setTotalPages,
    totalPages,
    handleUpdateList,
    count,
    setCount,
  } = usePagination({ perPage: 15 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showManualCloseModal, setShowManualCloseModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] =
    useState<null | IAttendance>(null);
  const [lastAttendance, setLastAttendance] = useState<IAttendance | undefined>(
    undefined,
  );
  const [data, setData] = useState<IAttendance[] | undefined>(undefined);
  const columns = useAttendanceColumns(
    setSelectedAttendance,
    setShowDeleteModal,
  );
  const handleStartAttendance = async () => {
    if (!lastAttendance?.endDate && lastAttendance?.id) {
      await AdminAttendanceService.end(
        new Date()?.toISOString(),
        lastAttendance?.id,
      )
        .then((response) => {
          if (response) {
            enqueueSnackbar(t('toasts.successfullyFinishedAttendance'), {
              variant: 'success',
            });
            handleUpdateList();
          }
        })
        .catch((err) => handleError(err));
    } else {
      await AdminAttendanceService.start(new Date()?.toISOString())
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(t('toasts.successfullyStartedAttendance'), {
              variant: 'success',
            });
          }
        })
        .catch((err) => handleError(err));
    }
  };

  const closeDeleteModal = () => {
    setSelectedAttendance(null);
    setShowDeleteModal(false);
  };

  const deleteAttendanceHandler = async (selectedAttendanceId: number) => {
    !!selectedAttendanceId &&
      (await AdminAttendanceService.delete(selectedAttendanceId)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', {
                title: selectedAttendanceId,
              }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch((err) => handleError(err)));
  };
  const handleCloseManualModal = () => {
    setSelectedAttendance(null);
    setShowManualCloseModal(false);
  };
  const handleUpdateAttendance = async (attendanceId: number) => {
    await AdminAttendanceService.end(
      String(selectedAttendance?.endDate),
      attendanceId,
      true,
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar(t('toasts.successfullyFinishedAttendance'), {
            variant: 'success',
          });
          handleCloseManualModal();
          handleUpdateList();
        }
      })
      .catch((err) => handleError(err));
  };

  useEffect(() => {
    AdminAttendanceService.getAll({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        const list = response?.data;
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(list);
        setLastAttendance(response?.lastAttendance);
      })
      .catch((err) => handleError(err));
  }, [pagination, setCount, setTotalPages]);
  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: '20px',
          }}
        >
          <Typography
            sx={{
              visibility: !lastAttendance?.endDate ? 'visible' : 'hidden',
              width: { xs: '100%', md: 'auto' },
              paddingBottom: { xs: 2, md: 0 },
            }}
          >
            {t('attendance.time_logged_in')}:{' '}
            {moment(
              lastAttendance?.startDate,
              undefined,
              i18n.language,
            ).fromNow()}
            !
          </Typography>
          <Box>
            {!lastAttendance?.endDate &&
              moment(lastAttendance?.startDate).isBefore(
                moment().subtract(10, 'hours'),
              ) && (
                <Button
                  variant={'contained'}
                  color={'error'}
                  onClick={() => {
                    setShowManualCloseModal(true);
                    setSelectedAttendance(lastAttendance ?? null);
                  }}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    mr: '15px',
                    mb: { xs: '15px', md: '0' },
                  }}
                >
                  <DoNotDisturbAltOutlined sx={{ mr: '5px' }} />
                  {t('button.changeStopTime')}
                </Button>
              )}
            <Button
              variant={'contained'}
              color={!lastAttendance?.endDate ? 'secondary' : 'primary'}
              onClick={handleStartAttendance}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <AddCircleOutlineOutlinedIcon sx={{ marginRight: '5px' }} />
              {t(
                !lastAttendance?.endDate
                  ? 'button.stopAttendance'
                  : 'button.startAttendance',
              )}
            </Button>
          </Box>
        </Box>
        <CustomDataGrid
          hasNextPage={totalPages < pagination?.page}
          rows={data || []}
          columns={columns}
          rowCount={count}
          pagination={pagination}
          setPagination={setPagination}
          loading={data === undefined}
        />
      </Card>
      <ConfirmDelete
        open={showDeleteModal}
        title={`ID: ${selectedAttendance?.id}`}
        handleClose={closeDeleteModal}
        handleSubmit={() =>
          deleteAttendanceHandler(Number(selectedAttendance?.id))
        }
      />
      <AttendanceManualFormModal
        attendance={selectedAttendance}
        setLastAttendance={setSelectedAttendance}
        open={showManualCloseModal}
        handleClose={handleCloseManualModal}
        handleSubmit={() =>
          handleUpdateAttendance(Number(selectedAttendance?.id))
        }
      />
    </>
  );
};

export default AdminAttendance;
